import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Icon from "@mdi/react";
import { mdiChevronDown } from "@mdi/js";
import { FOOTER } from "./mock";
import brandAwardsApi, { BrandAward } from "../../../api-services/brand-awards-api.service";

// A footer column. On desktop the heading and its links are always shown. On
// mobile (see v2.scss) the heading becomes a tappable accordion toggle and the
// body collapses — only when `collapsible` is set. Non-collapsible columns
// (Support, Payment, Follow) stay expanded on every breakpoint.
const FooterCol: React.FC<{
  title: string;
  collapsible?: boolean;
  className?: string;
  children: React.ReactNode;
}> = ({ title, collapsible = false, className = "", children }) => {
  const [open, setOpen] = useState(false);

  return (
    <div
      className={`gd-footer-v2__col ${collapsible ? "gd-footer-v2__col--accordion" : ""} ${className}`.trim()}
      data-open={open}
    >
      {collapsible ? (
        <button type="button" className="gd-footer-v2__col-head" onClick={() => setOpen((v) => !v)}>
          <h4>{title}</h4>
          <Icon className="gd-footer-v2__col-chevron" path={mdiChevronDown} size="20px" />
        </button>
      ) : (
        <h4>{title}</h4>
      )}
      <div className="gd-footer-v2__col-body">{children}</div>
    </div>
  );
};

// Award logos wrap onto as many rows as the viewport needs. Past this many
// rows the block collapses behind a "See more" toggle (mobile included, since
// the narrower column wraps sooner and hits the limit more often).
const AWARDS_COLLAPSED_ROWS = 2;

// Redesigned GD site footer. Rendered with the redesigned /web homepage.
export default function Footer() {
  const [awards, setAwards] = useState<BrandAward[]>([]);
  const [awardsExpanded, setAwardsExpanded] = useState(false);
  const [awardsOverflowing, setAwardsOverflowing] = useState(false);
  const awardLogosRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let active = true;

    brandAwardsApi.getAwards()
      .then((data) => {
        if (active) setAwards(data.filter((award) => award.image?.thumb || award.image?.original));
      })
      .catch(() => {
        if (active) setAwards([]);
      });

    return () => {
      active = false;
    };
  }, []);

  // Row count depends on rendered logo widths (only known once each image has
  // loaded) and on viewport width, so it's measured from the DOM rather than
  // computed from the award count.
  const checkAwardsOverflow = useCallback(() => {
    const container = awardLogosRef.current;
    if (!container) return;
    const tops = Array.from(container.querySelectorAll("img")).map((img) => img.offsetTop);
    const rowCount = new Set(tops).size;
    setAwardsOverflowing(rowCount > AWARDS_COLLAPSED_ROWS);
  }, []);

  useEffect(() => {
    checkAwardsOverflow();
    window.addEventListener("resize", checkAwardsOverflow);
    return () => window.removeEventListener("resize", checkAwardsOverflow);
  }, [awards, checkAwardsOverflow]);

  return (
    <footer id="gd-footer-v2">
      <div className="gd-footer-v2__inner">
        <div className="gd-footer-v2__top">
          <div className="gd-footer-v2__brand">
            <img src="/iceholidays-assets/images/GD_logo.png" alt="Golden Destinations" />
          </div>

          <FooterCol title="Our Brands" collapsible>
            {FOOTER.brands.map((b) => {
              if (!b.href) {
                return (
                  <span key={b.label} className="gd-footer-v2__link">{b.label}</span>
                );
              }
              // External brand sites open in a new tab; internal /web paths use
              // the SPA router.
              return b.href.startsWith("http") ? (
                <a
                  key={b.label}
                  href={b.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="gd-footer-v2__link"
                >
                  {b.label}
                </a>
              ) : (
                <Link key={b.label} to={b.href} className="gd-footer-v2__link">{b.label}</Link>
              );
            })}
          </FooterCol>

          <FooterCol title="Company" collapsible>
            {FOOTER.company.map((c) => (
              <Link key={c.label} to={c.href} className="gd-footer-v2__link">{c.label}</Link>
            ))}
          </FooterCol>

          <FooterCol title="Support" className="gd-footer-v2__col--support">
            {FOOTER.support.map((c) => (
              <Link key={c.label} to={c.href} className="gd-footer-v2__link">{c.label}</Link>
            ))}
          </FooterCol>

          <FooterCol title="Payment Method" className="gd-footer-v2__col--payment">
            <div className="gd-footer-v2__payments">
              {FOOTER.payments.map((p) => (
                <img key={p} src={p} alt="Payment method" />
              ))}
            </div>
            <div className="gd-footer-v2__follow">
              <h4 className="gd-footer-v2__section-title">Follow Us</h4>
              <div className="gd-footer-v2__socials">
                {FOOTER.socials.map((s) => (
                  <a key={s.icon} href={s.href} target="_blank" rel="noopener noreferrer">
                    <img src={s.icon} alt="Social link" />
                  </a>
                ))}
              </div>
            </div>
          </FooterCol>
        </div>

        <div className="gd-footer-v2__band">
          {awards.length > 0 && (
            <div className="gd-footer-v2__awards">
              <h4 className="gd-footer-v2__section-title">Recognised for Excellence</h4>
              <div
                ref={awardLogosRef}
                className={`gd-footer-v2__award-logos ${awardsOverflowing && !awardsExpanded ? "gd-footer-v2__award-logos--collapsed" : ""}`.trim()}
              >
                {awards.map((award) => (
                  <img
                    key={award.id}
                    src={award.image?.thumb || award.image?.original}
                    alt={award.title}
                    onLoad={checkAwardsOverflow}
                  />
                ))}
              </div>
              {awardsOverflowing && (
                <button
                  type="button"
                  className="gd-footer-v2__awards-toggle"
                  data-open={awardsExpanded}
                  onClick={() => setAwardsExpanded((v) => !v)}
                >
                  {awardsExpanded ? "See less" : "See more"}
                  <Icon className="gd-footer-v2__awards-toggle-chevron" path={mdiChevronDown} size="18px" />
                </button>
              )}
            </div>
          )}
        </div>

        <div className="gd-footer-v2__accreditations">
          {FOOTER.accreditations.map((a) => (
            <img key={a} src={a} alt="Accreditation" />
          ))}
        </div>

        <div className="gd-footer-v2__bottom">
          <span>Copyright © 2026 Golden Destinations. All rights reserved.</span>
          <div className="gd-footer-v2__bottom-links">
            <Link to="/web/footer/privacy">Privacy</Link>
            <Link to="/web/footer/terms">Terms</Link>
            <span>Cookie settings</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
